import { useCallback, useEffect, useState } from "react";
import { useAuth } from "./useAuth";
import { EXTERNAL_SUPABASE_URL, EXTERNAL_SUPABASE_PUBLISHABLE_KEY } from "@/integrations/external-supabase/client";
import { makeOAuthState, rememberPinterestOAuth } from "@/lib/pinterestOAuth";

/**
 * #1073 — Pinterest connection state for the Settings row and the inspo sheet.
 *
 * A plain hook, not a provider: only two screens use it and neither is mounted
 * for long, so each asks the server when it opens. Nothing Pinterest returns is
 * kept past the component that asked — their storage rule.
 *
 * Token from useAuth(), never supabase.auth.getSession() (CLAUDE.md, the June 29
 * lock-starvation incident).
 */

export const PINTEREST_AUTH_URL = `${EXTERNAL_SUPABASE_URL}/functions/v1/pinterest-auth`;

export interface PinterestBoard {
  id: string;
  name: string;
  pin_count: number | null;
  cover_url: string | null;
}

export type BoardsResult =
  | { ok: true; boards: PinterestBoard[] }
  | { ok: false; message: string; notConnected?: boolean };

export function usePinterest() {
  const { user, session } = useAuth();
  const [connected, setConnected] = useState(false);
  // A token row exists, usable or not. Differs from `connected` when Pinterest
  // stopped honouring her token: Settings still needs to offer Disconnect.
  const [stored, setStored] = useState(false);
  const [checking, setChecking] = useState(true);

  const call = useCallback(
    async (payload: Record<string, unknown>): Promise<{ ok: boolean; status: number; data: Record<string, unknown> }> => {
      const token = session?.access_token;
      if (!token) return { ok: false, status: 401, data: {} };
      const resp = await fetch(PINTEREST_AUTH_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          apikey: EXTERNAL_SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify(payload),
      });
      const data = (await resp.json().catch(() => ({}))) as Record<string, unknown>;
      return { ok: resp.ok, status: resp.status, data };
    },
    [session?.access_token],
  );

  const checkStatus = useCallback(async () => {
    if (!user || !session?.access_token) {
      setConnected(false);
      setStored(false);
      setChecking(false);
      return;
    }
    try {
      const res = await call({ action: "check_status" });
      // Only a real answer changes the state. A blip leaves the last known
      // value rather than flashing "Not connected".
      if (res.ok) {
        setConnected(res.data.connected === true);
        setStored(res.data.stored === true || res.data.connected === true);
      }
    } catch {
      /* network blip */
    } finally {
      setChecking(false);
    }
  }, [user, session?.access_token, call]);

  useEffect(() => {
    void checkStatus();
  }, [checkStatus]);

  /** Throws with a message she can read. Same-window redirect on success. */
  const connect = useCallback(async () => {
    const state = makeOAuthState();
    if (!rememberPinterestOAuth(state, window.location.pathname + window.location.search)) {
      throw new Error("Your browser blocked the step that keeps this safe. Try again outside private browsing.");
    }
    const res = await call({ action: "auth_url", state });
    const authUrl = res.data.authUrl;
    if (!res.ok || typeof authUrl !== "string" || !authUrl.startsWith("https://www.pinterest.com/oauth/")) {
      throw new Error(typeof res.data.error === "string" ? res.data.error : "Couldn't start connecting Pinterest.");
    }
    window.location.href = authUrl;
  }, [call]);

  /** Throws on failure, so a confirm dialog never closes over a no-op. */
  const disconnect = useCallback(async () => {
    const res = await call({ action: "disconnect" });
    if (!res.ok || res.data.success !== true) throw new Error("disconnect-failed");
    setConnected(false);
    setStored(false);
  }, [call]);

  const listBoards = useCallback(async (): Promise<BoardsResult> => {
    try {
      const res = await call({ action: "list_boards" });
      if (res.ok && Array.isArray(res.data.boards)) {
        return { ok: true, boards: res.data.boards as PinterestBoard[] };
      }
      if (res.data.code === "not_connected") {
        setConnected(false);
        return { ok: false, notConnected: true, message: String(res.data.error || "Reconnect Pinterest to pick a board.") };
      }
      return { ok: false, message: String(res.data.error || "Couldn't load your boards just now.") };
    } catch {
      return { ok: false, message: "Couldn't reach Pinterest. Check your connection 💜" };
    }
  }, [call]);

  return { connected, stored, checking, connect, disconnect, listBoards, checkStatus };
}
